import React, { useEffect, useState, useRef } from 'react';
import { Flex, Icon, Image, Stack, Text } from '@chakra-ui/react';
import { FormHandles } from '@unform/core';
import { FiDownload, FiPlus, FiSearch } from 'react-icons/fi';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import { AppError } from '../../utils/AppError';
import { formatPrice } from '../../utils/format';
import Loading from '../../components/Loading';
import Sidebar from '../../components/Sidebar';
import InputDate from '../../components/Inputs/InputDate';
import ModalFinanceiro from '../../components/ComponentsTitulo/ModalTitulo';
import ImgLoading from '../../assets/loading/tres_pontos.gif';
import ModalTituloEdit from '../../components/ComponentsTitulo/ModalTituloEdit';
import ModalTituloDelete from '../../components/ComponentsTitulo/ModalTituloDelete';
import ModalTituloBaixa from '../../components/ComponentsTitulo/ModalTituloBaixa';
import DTTitulo from '../../components/ComponentsTitulo/DataTableTitulo';
import { FinJsonDTO } from '../../dtos/FinJsonDTO';
import { TituloDTO } from '../../dtos/TituloDTO';

import { Container, Content, Header, Filtros, FormView, ListVazio } from './styles';

type FiltroData = {
	data_i: string;
	data_f: string;
	status: string;
}

const Titulo: React.FC = () => {
	const formRef = useRef<FormHandles>(null);

	const { company } = useAuth();
	const { addToast } = useToast();

	const [loading, setLoading] = useState(true);
	const [loadData, setLoadData] = useState(true);

	const [financeiros, setFinanceiros] = useState<TituloDTO[]>([]);
	const [radioStatus, setRadioStatus] = useState('A');

	const [modalTituloBaixaVisible, setModalTituloBaixaVisible] = useState(false);
	const [titulosBaixa, setTitulosBaixa] = useState<FinJsonDTO[]>([]);
	
	const [modalTituloVisible, setModalTituloVisible] = useState(false);
	const [modalTituloEditVisible, setModalTituloEditVisible] = useState(false);
	const [tituloModal, setTituloModal] = useState<FinJsonDTO>({} as FinJsonDTO);
	const [modalTituloDeleteVisible, setModalTituloDeleteVisible] = useState(false);

	async function fetchFinanceiros(filter: FiltroData) {
		try {
			setLoadData(true);

			const { data } = await api.post('/portal/financeiro', filter);

			const auxTitulos = data;

			for (let i = 0; i < auxTitulos.length; i++) {
				auxTitulos[i].amortizado_formatted = formatPrice(auxTitulos[i].amortizado);
				auxTitulos[i].desconto_formatted = formatPrice(auxTitulos[i].desconto);
				auxTitulos[i].juros_formatted = formatPrice(auxTitulos[i].juros);
				auxTitulos[i].multa_formatted = formatPrice(auxTitulos[i].multa);
				auxTitulos[i].pago_formatted = formatPrice(auxTitulos[i].pago);
				auxTitulos[i].saldo_cred_formatted = formatPrice(auxTitulos[i].saldo_cred);
				auxTitulos[i].saldo_deb_formatted = formatPrice(auxTitulos[i].saldo_deb);
				auxTitulos[i].vlr_cred_formatted = formatPrice(auxTitulos[i].vlr_cred);
				auxTitulos[i].vlr_deb_formatted = formatPrice(auxTitulos[i].vlr_deb);
			};

			setFinanceiros([...auxTitulos]);
		}
		catch (error) {
			console.log('ERROR ==> ', error);
			const isAppError = error instanceof AppError;
			const msg = isAppError ? error.message : 'Ocorreu um erro ao buscar dados da página.';

			addToast({
				type: 'error',
				title: 'Erro',
				description: msg,
			});
			
			setFinanceiros([]);
		}
		finally {
			setLoadData(false);
			setLoading(false);
		}
	};

	useEffect(() => {
		const fetch = async () => await fetchFinanceiros({ status: radioStatus, data_i: '', data_f: '' });
		fetch();
	}, [company]);

	// UseEffect para bloquear a rolagem ao abrir algum modal
	useEffect(() => {
		if (modalTituloVisible || modalTituloEditVisible || modalTituloDeleteVisible
			|| modalTituloBaixaVisible) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'auto';
		}
	}, [modalTituloVisible, modalTituloEditVisible, modalTituloDeleteVisible, modalTituloBaixaVisible]);

	async function handleForm() {
		const data_i = formRef.current?.getFieldValue('data_i');
		const data_f = formRef.current?.getFieldValue('data_f');

		const dados = {
			status: radioStatus,
			data_i: data_i,
			data_f: data_f
		};

		await fetchFinanceiros(dados);
	};

	return (
		<>
			<Container>
				<Sidebar />

				{loading ? (
					<Loading />
				) : (
					<Content>
						<Header>
							<Flex flexDir="column">
								<h1>Títulos</h1>
								<h2>Todo os seus Títulos aqui.</h2>
							</Flex>

							<Flex flexDir="column" justify="flex-end" pt={[2, 0]}>
								<Stack direction={["column", "row"]} pb={[0, 2]}>
									<button
										onClick={() => {
											setTituloModal({} as FinJsonDTO);
											setModalTituloEditVisible(true);
										}}
									>
										<Icon as={FiPlus} fontSize={20} />&nbsp;
										NOVO
									</button>
									&nbsp;&nbsp;&nbsp;
									<button
										disabled={titulosBaixa.length === 0}
										onClick={() => {
											setModalTituloBaixaVisible(true);
										}}
									>
										<Icon as={FiDownload} fontSize={20} />&nbsp;
										BAIXAR
									</button>
								</Stack>
							</Flex>
						</Header>

						<Filtros>
							<FormView ref={formRef} onSubmit={handleForm}>
								<Flex width="100%" justify="center" mr={[0, 0, 2, 2]}>
									<Flex w="100%" mr={1} flexDirection={["row", "column"]} justify="flex-end">
										<div className="input-content">
											<span>Data Inicial</span>
											<InputDate
												name="data_i"
												placeholder="Data Inicial"
												containerStyle={{ padding: '5px 7px' }}
											/>
										</div>
									</Flex>

									<Flex w="100%" ml={1} flexDirection={["row", "column"]} justify="flex-end">
										<div className="input-content">
											<span>Data Final</span>
											<InputDate
												name="data_f"
												placeholder="Data Final"
												containerStyle={{ padding: '5px 7px' }}
											/>
										</div>
									</Flex>
								</Flex>

								<Flex width="100%" mt={["10px", "10px", "0px"]}>
									<Flex width="100%">
										<div className="radio-group">
											<div className="radio-label" onClick={() => setRadioStatus('A')}>
												<input
													type="radio"
													value="A"
													checked={radioStatus === 'A'}
													onChange={() => setRadioStatus('A')}
												/>
												<label>Abertas</label>
											</div>

											<div className="radio-label" onClick={() => setRadioStatus('B')}>
												<input
													type="radio"
													value="B"
													checked={radioStatus === 'B'}
													onChange={() => setRadioStatus('B')}
												/>
												<label>Baixadas</label>
											</div>

											<div className="radio-label" onClick={() => setRadioStatus('T')}>
												<input
													type="radio"
													value="T"
													checked={radioStatus === 'T'}
													onChange={() => setRadioStatus('T')}
												/>
												<label>Todas</label>
											</div>
										</div>
									</Flex>

									<Flex flexDirection="column" justify="flex-end">
										<button type="submit">
											<FiSearch /> <span>Pesquisar</span>
										</button>
									</Flex>
								</Flex>
							</FormView>
						</Filtros>

						{loadData ? (
							<Flex w="100%" justify="center" align="center">
								<Image src={ImgLoading} w="100px" h="100px" />
							</Flex>
						) : (financeiros.length > 0 ? (
								<DTTitulo
									data={financeiros}
									setTituloModal={setTituloModal}
									setModalTituloVisible={setModalTituloVisible}
									setModalTituloEditVisible={setModalTituloEditVisible}
									setModalTituloDeleteVisible={setModalTituloDeleteVisible}
									titulosBaixa={titulosBaixa}
									setTitulosBaixa={setTitulosBaixa}
								/>
							) : (
								<ListVazio>
									<Text fontSize={18}>Nenhum financeiro neste período.</Text>
								</ListVazio>
							)
						)}
					</Content>
				)}
			</Container>

			{modalTituloVisible && (
				<ModalFinanceiro
					onClose={() => setModalTituloVisible(false)}
					isVisible={modalTituloVisible}
					titulo={tituloModal}
				/>
			)}

			{modalTituloEditVisible && (
				<ModalTituloEdit
					onClose={() => setModalTituloEditVisible(false)}
					isVisible={modalTituloEditVisible}
					titulo={tituloModal}
					handleForm={handleForm}
				/>
			)}

			{modalTituloDeleteVisible && (
				<ModalTituloDelete
					onClose={() => setModalTituloDeleteVisible(false)}
					isVisible={modalTituloDeleteVisible}
					titulo={tituloModal}
					handleForm={handleForm}
				/>
			)}

			{modalTituloBaixaVisible && (
				<ModalTituloBaixa
					onClose={() => setModalTituloBaixaVisible(false)}
					isVisible={modalTituloBaixaVisible}
					titulos={titulosBaixa}
					handleForm={handleForm}
					setTitulosParaBaixa={setTitulosBaixa}
				/>
			)}
		</>
	)
}

export default Titulo;